import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { Helmet } from "react-helmet-async";

import { I18nProvider } from "./lib/i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToHash from "./components/ScrollToHash";
import DemoTourGate from "./components/DemoTourGate";
import { trackPageView, capturePartnerReferral } from "./lib/events";
import { initConsent } from "./lib/consent";
import CookieConsentBanner from "./components/CookieConsentBanner";
import StagingBanner from "./components/StagingBanner";

// Public — eagerly loaded (première peinture rapide)
import CmsPagePublic from "./pages/public/CmsPagePublic";
import CartDrawer from "./components/CartDrawer";

// Redirection legacy pour l'ancien URL /le-haras/location-de-vehicules/:id
function VehicleLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/haras/location-camion-chevaux/${id}`} replace />;
}

// Public — routes secondaires (lazy)
const TrouveTaPlacePage = lazy(() => import("./pages/public/TrouveTaPlacePage"));
const DemoLandingPage = lazy(() => import("./pages/DemoLandingPage"));
const DemoDomainePage = lazy(() => import("./pages/DemoDomainePage"));
const DemoFinalPage = lazy(() => import("./pages/DemoFinalPage"));
const VadCardPublicPage = lazy(() => import("./pages/public/VadCardPublicPage"));
const VadSuccessPage = lazy(() => import("./pages/public/VadSuccessPage"));
const CartPage = lazy(() => import("./pages/public/CartPage"));
const MultiCartSuccessPage = lazy(() => import("./pages/public/MultiCartSuccessPage"));
const AmenitizRoomsPage = lazy(() => import("./pages/public/AmenitizRoomsPage"));
const BookingPage = lazy(() => import("./pages/public/BookingPage"));
const AnimationsPage = lazy(() => import("./pages/public/AnimationsPage"));
const AnimationDetailPage = lazy(() => import("./pages/public/AnimationDetailPage"));
const AnimationsAdminPage = lazy(() => import("./pages/admin/AnimationsAdminPage"));
const VehicleRentalRenterPage = lazy(() => import("./pages/public/VehicleRentalRenterPage"));
const VehicleStateQRPage = lazy(() => import("./pages/public/VehicleStateQRPage"));
const PublicVehicleListPage = lazy(() => import("./pages/public/PublicVehicleListPage"));
const PublicVehicleDetailPage = lazy(() => import("./pages/public/PublicVehicleDetailPage"));
const PreviewPage = lazy(() => import("./pages/public/PreviewPage"));
const NousRejoindrePage = lazy(() => import("./pages/public/NousRejoindrePage"));
const PartnerFichePage = lazy(() => import("./pages/public/PartnerFichePage"));
const PartnerPortalPage = lazy(() => import("./pages/public/PartnerPortalPage"));
const VendorEventPortalPage = lazy(() => import("./pages/public/VendorEventPortalPage"));
const PartnerCategoryPage = lazy(() => import("./pages/public/PartnerCategoryPage"));
const EmergencyAccessPage = lazy(() => import("./pages/EmergencyAccessPage"));
const WeddingLandingPage = lazy(() => import("./pages/public/WeddingLandingPage"));
const WeddingLandingMerciPage = lazy(() => import("./pages/public/WeddingLandingMerciPage"));

// Public — Journal/Événements
const PublicListPages = () => import("./pages/public/PublicListPages");
const JournalListPage = lazy(() => PublicListPages().then((m) => ({ default: m.JournalListPage })));
const JournalArticlePage = lazy(() => PublicListPages().then((m) => ({ default: m.JournalArticlePage })));
const ActualitesListPage = lazy(() => import("./pages/public/ActualitesPages").then((m) => ({ default: m.ActualitesListPage })));
const ActualiteArticlePage = lazy(() => import("./pages/public/ActualitesPages"));
const NewsDashboardPage = lazy(() => import("./pages/admin/NewsDashboardPage"));
const NewsArticlesPage = lazy(() => import("./pages/admin/NewsArticlesPage"));
const NewsArticleEditorPage = lazy(() => import("./pages/admin/NewsArticleEditorPage"));
const NewsPerformancesPage = lazy(() => import("./pages/admin/NewsPerformancesPage"));
const NewsAiAssistantPage = lazy(() => import("./pages/admin/NewsAiAssistantPage"));
const NewsSeoPage = lazy(() => import("./pages/admin/NewsSeoPage"));
const NewsMonthlyReportPage = lazy(() => import("./pages/admin/NewsMonthlyReportPage"));
const NewsKnowledgeBasePage = lazy(() => import("./pages/admin/NewsKnowledgeBasePage"));
function JournalRedirect() { const { slug } = useParams(); return <Navigate to={`/actualites/${slug}`} replace />; }
// LOT A — A9 : ancienne adresse des lieux conservée (liens déjà envoyés) → nouvelle adresse « Imaginer »
function ClientVenuesLegacyRedirect() { const { search, hash } = useLocation(); return <Navigate to={`/mon-evenement/imaginer/lieux${search}${hash}`} replace />; }
const EventFichePage = lazy(() => PublicListPages().then((m) => ({ default: m.EventFichePage })));
const EventsPage = lazy(() => PublicListPages().then((m) => ({ default: m.EventsPage })));
const HorsesPage = lazy(() => PublicListPages().then((m) => ({ default: m.HorsesPage })));
const HorseFichePage = lazy(() => import("./pages/public/HorseFichePage"));
const EventsPrivatisationsPage = lazy(() => import("./pages/public/EventsPrivatisationsPage"));

// Admin — full bundle lazy (le visiteur public ne le télécharge pas)
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const ReviewsPage = lazy(() => import("./pages/admin/ReviewsPage"));
const AuditPage = lazy(() => import("./pages/admin/AuditPage"));
const InquiriesPage = lazy(() => import("./pages/admin/InquiriesPage"));
const EntityListPage = lazy(() => import("./pages/admin/EntityListPage"));
const EntityEditorPage = lazy(() => import("./pages/admin/EntityEditorPage"));
const MediaUploadPage = lazy(() => import("./pages/admin/MediaUploadPage"));
const ImportRedirectsPage = lazy(() => import("./pages/admin/ImportRedirectsPage"));
const SystemUsersPage = lazy(() => import("./pages/admin/SystemUsersPage"));
const SystemSmtpPage = lazy(() => import("./pages/admin/SystemSmtpPage"));
const SystemArchitecturePage = lazy(() => import("./pages/admin/SystemArchitecturePage"));
const SystemChangelogPage = lazy(() => import("./pages/admin/SystemChangelogPage"));
const SystemIntegrationsPage = lazy(() => import("./pages/admin/SystemIntegrationsPage"));
const SystemPageImagesPage = lazy(() => import("./pages/admin/SystemPageImagesPage"));
const HarasRemindersPage = lazy(() => import("./pages/admin/HarasRemindersPage"));
const VadCardsListPage = lazy(() => import("./pages/admin/VadCardsListPage"));
const VadStatsPage = lazy(() => import("./pages/admin/VadStatsPage"));
const CmsDashboardPage = lazy(() => import("./pages/admin/CmsDashboardPage"));
const CmsPagesTreePage = lazy(() => import("./pages/admin/CmsPagesTreePage"));
const ContentAuditPage = lazy(() => import("./pages/admin/ContentAuditPage"));
const CmsMenusPage = lazy(() => import("./pages/admin/CmsMenusPage"));
const CmsPageEditorPage = lazy(() => import("./pages/admin/CmsPageEditorPage"));
const CmsVariablesPage = lazy(() => import("./pages/admin/CmsVariablesPage"));
const CmsPricingPage = lazy(() => import("./pages/admin/CmsPricingPage"));
const TourismPage = lazy(() => import("./pages/admin/TourismPage"));
const CmsRedirectsPage = lazy(() => import("./pages/admin/CmsRedirectsPage"));
const AccommodationsTreePage = lazy(() => import("./pages/admin/AccommodationsTreePage"));
const AccommodationsUniversPage = lazy(() => import("./pages/admin/AccommodationsUniversPage"));
const AccommodationUnitEditorPage = lazy(() => import("./pages/admin/AccommodationUnitEditorPage"));
const VadCardEditorPage = lazy(() => import("./pages/admin/VadCardEditorPage"));
const VadScannerPage = lazy(() => import("./pages/admin/VadScannerPage"));
const VadSettingsPage = lazy(() => import("./pages/admin/VadSettingsPage"));
const VadFamiliesPage = lazy(() => import("./pages/admin/VadFamiliesPage"));
const VadTagsPage = lazy(() => import("./pages/admin/VadTagsPage"));
const SystemAssistantPage = lazy(() => import("./pages/admin/SystemAssistantPage"));
const SystemParametersPage = lazy(() => import("./pages/admin/SystemParametersPage"));
const SystemEmailTemplatesPage = lazy(() => import("./pages/admin/SystemEmailTemplatesPage"));
const SystemEmailSignaturePage = lazy(() => import("./pages/admin/SystemEmailSignaturePage"));
const SystemBackupsPage = lazy(() => import("./pages/admin/SystemBackupsPage"));
const ClientEventsListPage = lazy(() => import("./pages/admin/ClientEventsListPage"));
const ClientEventEditorPage = lazy(() => import("./pages/admin/ClientEventEditorPage"));
const ContactsPage = lazy(() => import("./pages/admin/ContactsPage"));
const PartnersPage = lazy(() => import("./pages/admin/PartnersPage"));
const PartnerNetworkPage = lazy(() => import("./pages/admin/PartnerNetworkPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const MediaGridPage = lazy(() => import("./pages/admin/MediaGridPage"));
const VehicleRentalDashboardPage = lazy(() => import("./pages/admin/VehicleRentalDashboardPage"));
const VehicleEditorPage = lazy(() => import("./pages/admin/VehicleEditorPage"));
const RenterEditorPage = lazy(() => import("./pages/admin/RenterEditorPage"));
const VehicleReservationEditorPage = lazy(() => import("./pages/admin/VehicleReservationEditorPage"));
const VehicleSettingsPage = lazy(() => import("./pages/admin/VehicleSettingsPage"));
const EventBillingSettingsPage = lazy(() => import("./pages/admin/EventBillingSettingsPage"));
const EventBillingClientPage = lazy(() => import("./pages/public/EventBillingClientPage"));
const AccountingPage = lazy(() => import("./pages/admin/AccountingPage"));
const LegalSettingsPage = lazy(() => import("./pages/admin/LegalSettingsPage"));
const WeddingLandingSettingsPage = lazy(() => import("./pages/admin/WeddingLandingSettingsPage"));
const WeddingLandingLeadsPage = lazy(() => import("./pages/admin/WeddingLandingLeadsPage"));

// Client (magic link)
const ClientLayout = lazy(() => import("./pages/client/ClientLayout"));
const ClientLoginPage = lazy(() => import("./pages/client/ClientLoginPage"));
const ClientVerifyPage = lazy(() => import("./pages/client/ClientVerifyPage"));
const ClientDashboardPage = lazy(() => import("./pages/client/ClientDashboardPage"));
const ClientContactsPage = lazy(() => import("./pages/client/ClientContactsPage"));
const ClientBookingsPage = lazy(() => import("./pages/client/ClientBookingsPage"));
const ClientMembersPage = lazy(() => import("./pages/client/ClientMembersPage"));
const ClientDayPlanPage = lazy(() => import("./pages/client/ClientDayPlanPage"));
const ClientParticipantsPage = lazy(() => import("./pages/client/ClientParticipantsPage"));
const ClientDossierPage = lazy(() => import("./pages/client/ClientDossierPage"));
const ClientChecklistPage = lazy(() => import("./pages/client/ClientChecklistPage"));
const ClientBudgetPage = lazy(() => import("./pages/client/ClientBudgetPage"));
const ClientQuotesPage = lazy(() => import("./pages/client/ClientQuotesPage"));
const ClientVendorsPage = lazy(() => import("./pages/client/ClientVendorsPage"));
const ClientRoomPlansPage = lazy(() => import("./pages/client/ClientRoomPlansPage"));
const ClientThemePage = lazy(() => import("./pages/client/ClientThemePage"));
const ClientMealsPage = lazy(() => import("./pages/client/ClientMealsPage"));
const ClientRoomingPage = lazy(() => import("./pages/client/ClientRoomingPage"));
const ClientComptePage = lazy(() => import("./pages/client/ClientComptePage"));
const ClientOptionsPage = lazy(() => import("./pages/client/ClientOptionsPage"));
const ClientActivityPage = lazy(() => import("./pages/client/ClientActivityPage"));
const DomainRulesPage = lazy(() => import("./pages/admin/DomainRulesPage"));
const EventOptionsCatalogPage = lazy(() => import("./pages/admin/EventOptionsCatalogPage"));
const RsvpPage = lazy(() => import("./pages/public/RsvpPage"));

// AssistantWidget seulement pour l'extranet
const AssistantWidget = lazy(() => import("./components/AssistantWidget"));

const RouteFallback = () => (
  <div className="min-h-screen bg-lin flex items-center justify-center text-ardoise/40 text-sm">
    Chargement…
  </div>
);

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    capturePartnerReferral();
    initConsent();
  }, []);
  useEffect(() => {
    // Track page_view sur chaque changement de route (hors /extranet & /mon-evenement,
    // filtre appliqué dans track()).
    trackPageView();
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isExtranet = location.pathname.startsWith("/extranet");
  return (
    <>
      <ScrollToHash />
      <StagingBanner />
      <DemoTourGate />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public — Homepage, listes ET fiches gérées par le CMS (catch-all).
              Les fiches dynamiques (accommodation, horse) sont rendues par CmsPagePublic
              qui délègue au bon composant si aucune page CMS ne matche. */}
          <Route path="/actualites" element={<ActualitesListPage />} />
          <Route path="/actualites/:slug" element={<ActualiteArticlePage />} />
          <Route path="/journal" element={<Navigate to="/actualites" replace />} />
          <Route path="/journal/:slug" element={<JournalRedirect />} />
          <Route path="/evenements-prives" element={<EventsPrivatisationsPage />} />
          <Route path="/evenements" element={<EventsPage />} />
          <Route path="/evenements/:slug" element={<EventFichePage />} />
          <Route path="/preview/:token" element={<PreviewPage />} />
          <Route path="/nous-rejoindre" element={<NousRejoindrePage />} />
          <Route path="/partenaires/:slug" element={<PartnerFichePage />} />
          <Route path="/partenaires/categorie/:catSlug" element={<PartnerCategoryPage />} />
          <Route path="/espace-partenaire/:token" element={<PartnerPortalPage />} />
          <Route path="/espace-prestataire/:token" element={<VendorEventPortalPage />} />
          <Route path="/emergency-access" element={<EmergencyAccessPage />} />
          <Route path="/trouve-ta-place/:token" element={<TrouveTaPlacePage />} />
          <Route path="/rsvp/:token" element={<RsvpPage />} />
          <Route path="/demo-mariage" element={<DemoLandingPage />} />
          <Route path="/demo-mariage/domaine" element={<DemoDomainePage />} />
          <Route path="/demo-mariage/fin" element={<DemoFinalPage />} />
          <Route path="/carte/:token" element={<VadCardPublicPage />} />
          <Route path="/carte/:token/success" element={<VadSuccessPage />} />
          <Route path="/panier" element={<CartPage />} />
          <Route path="/panier/success" element={<MultiCartSuccessPage />} />
          <Route path="/hebergements/amenitiz" element={<AmenitizRoomsPage />} />
          <Route path="/reserver" element={<BookingPage />} />
          <Route path="/seminaires/animations" element={<AnimationsPage />} />
          <Route path="/seminaires/animations/:slug" element={<AnimationDetailPage />} />
          <Route path="/ma-location/:token" element={<VehicleRentalRenterPage />} />
          <Route path="/vehicules/etat/:vehicleId" element={<VehicleStateQRPage />} />
          <Route path="/mon-evenement-facturation/:token" element={<EventBillingClientPage />} />
          <Route path="/le-haras/location-de-vehicules" element={<Navigate to="/haras/location-camion-chevaux" replace />} />
          <Route path="/le-haras/location-de-vehicules/:id" element={<VehicleLegacyRedirect />} />
          <Route path="/haras/location-camion-chevaux" element={<PublicVehicleListPage />} />
          <Route path="/haras/location-camion-chevaux/:id" element={<PublicVehicleDetailPage />} />
          <Route path="/haras/chevaux" element={<HorsesPage />} />
          <Route path="/haras/chevaux/:slug" element={<HorseFichePage />} />
          <Route path="/domaine-mariage-poitiers" element={<WeddingLandingPage />} />
          <Route path="/domaine-mariage-poitiers/merci" element={<WeddingLandingMerciPage />} />

          {/* Extranet CLIENT (magic link, /mon-evenement) */}
          <Route path="/mon-evenement/login" element={<ClientLoginPage />} />
          <Route path="/mon-evenement/verify" element={<ClientVerifyPage />} />
          <Route path="/mon-evenement" element={<ClientLayout />}>
            <Route index element={<ClientDashboardPage />} />
            <Route path="participants" element={<ClientParticipantsPage />} />
            <Route path="repas" element={<ClientMealsPage />} />
            <Route path="rooming" element={<ClientRoomingPage />} />
            <Route path="organisation" element={<ClientChecklistPage />} />
            <Route path="prestataires" element={<ClientVendorsPage />} />
            <Route path="contacts" element={<ClientContactsPage />} />
            <Route path="imaginer/lieux" element={<ClientBookingsPage />} />
            <Route path="hebergements" element={<ClientVenuesLegacyRedirect />} />
            <Route path="plan-de-salle" element={<ClientRoomPlansPage />} />
            <Route path="theme-deco" element={<ClientThemePage />} />
            <Route path="dossier" element={<ClientDossierPage />} />
            <Route path="budget" element={<ClientBudgetPage />} />
            <Route path="devis" element={<ClientQuotesPage />} />
            <Route path="compte" element={<ClientComptePage />} />
            <Route path="options" element={<ClientOptionsPage />} />
            <Route path="activite" element={<ClientActivityPage />} />
            <Route path="invites" element={<ClientMembersPage />} />
            <Route path="jour-j" element={<ClientDayPlanPage />} />
          </Route>

          {/* Extranet COLLABORATEUR */}
          <Route path="/extranet" element={<AdminLoginPage />} />
          <Route
            path="/extranet/*"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="tableau-de-bord" element={<DashboardPage />} />

            {/* Specialized pages */}
            <Route path="parametres" element={<SettingsPage />} />
            <Route path="avis-clients" element={<ReviewsPage />} />
            <Route path="system/utilisateurs" element={
              <ProtectedRoute requireRole={["admin"]}><SystemUsersPage /></ProtectedRoute>
            } />
            <Route path="system/smtp" element={<SystemSmtpPage />} />
            <Route path="system/architecture" element={<SystemArchitecturePage />} />
            <Route path="system/changelog" element={<SystemChangelogPage />} />
            <Route path="system/integrations" element={<SystemIntegrationsPage />} />
            <Route path="system/page-images" element={<SystemPageImagesPage />} />
            <Route path="haras/rappels" element={<HarasRemindersPage />} />
            <Route path="site" element={<CmsDashboardPage />} />
            <Route path="site/pages" element={<CmsPagesTreePage />} />
            <Route path="site/controle-contenus" element={<ContentAuditPage />} />
            <Route path="site/pages/:pageId" element={<CmsPageEditorPage />} />
            <Route path="site/menus" element={<CmsMenusPage />} />
            <Route path="site/variables" element={<CmsVariablesPage />} />
            <Route path="site/tarifs" element={<CmsPricingPage />} />
            <Route path="site/tourisme" element={<TourismPage />} />
            <Route path="site/redirections" element={<CmsRedirectsPage />} />
            <Route path="site/landing-mariage" element={<WeddingLandingSettingsPage />} />
            <Route path="site/landing-mariage/demandes" element={<WeddingLandingLeadsPage />} />
            <Route path="actualites-seo" element={<NewsDashboardPage />} />
            <Route path="actualites-seo/performances" element={<NewsPerformancesPage />} />
            <Route path="actualites-seo/assistant-ia" element={<NewsAiAssistantPage />} />
            <Route path="actualites-seo/ia-seo" element={<NewsSeoPage />} />
            <Route path="actualites-seo/bilan-mensuel" element={<NewsMonthlyReportPage />} />
            <Route path="actualites-seo/base-de-connaissances" element={<NewsKnowledgeBasePage />} />
            <Route path="actualites-seo/articles" element={<NewsArticlesPage />} />
            <Route path="actualites-seo/articles/:articleId" element={<NewsArticleEditorPage />} />
            <Route path="hebergements-arbre" element={<AccommodationsTreePage />} />
            <Route path="univers/hebergements" element={<AccommodationsUniversPage />} />
            <Route path="univers/animations" element={<AnimationsAdminPage />} />
            <Route path="univers/hebergements/:slug" element={<AccommodationUnitEditorPage />} />
            <Route path="vad/cartes" element={<VadCardsListPage />} />
            <Route path="vad/stats" element={<VadStatsPage />} />
            <Route path="vad/cartes/:id" element={<VadCardEditorPage />} />
            <Route path="vad/scan" element={<VadScannerPage />} />
            <Route path="vad/parametres" element={<VadSettingsPage />} />
            <Route path="vad/familles" element={<VadFamiliesPage />} />
            <Route path="vad/tags" element={<VadTagsPage />} />
            <Route path="system/assistant" element={<SystemAssistantPage />} />
            <Route path="system/event-catalog" element={<SystemParametersPage />} />
            <Route path="system/parametres" element={<SystemParametersPage />} />
            <Route path="system/email-templates" element={<SystemEmailTemplatesPage />} />
            <Route path="system/email-signature" element={<SystemEmailSignaturePage />} />
            <Route path="system/sauvegardes" element={<SystemBackupsPage />} />
            <Route path="evenements-clients" element={<ClientEventsListPage />} />
            <Route path="evenements-clients/parametres" element={<EventBillingSettingsPage />} />
            <Route path="evenements-clients/regles" element={<DomainRulesPage />} />
            <Route path="evenements-clients/options" element={<EventOptionsCatalogPage />} />
            <Route path="evenements-clients/:id" element={<ClientEventEditorPage />} />
            <Route path="comptabilite" element={<AccountingPage />} />
            <Route path="comptabilite/informations-legales" element={<LegalSettingsPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="partenaires" element={<PartnersPage />} />
            <Route path="partenaires/reseau" element={<PartnerNetworkPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="media" element={<MediaGridPage />} />
            <Route path="vehicules" element={<VehicleRentalDashboardPage />} />
            <Route path="vehicules/nouveau" element={<VehicleEditorPage />} />
            <Route path="vehicules/:id" element={<VehicleEditorPage />} />
            <Route path="locataires/nouveau" element={<RenterEditorPage />} />
            <Route path="locataires/:id" element={<RenterEditorPage />} />
            <Route path="vehicule-reservations/nouveau" element={<VehicleReservationEditorPage />} />
            <Route path="vehicule-reservations/:id" element={<VehicleReservationEditorPage />} />
            <Route path="vehicules/parametres" element={<VehicleSettingsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="inquiries" element={<InquiriesPage />} />
            <Route path="import-redirects" element={
              <ProtectedRoute requireRole={["admin"]}><ImportRedirectsPage /></ProtectedRoute>
            } />

            {/* Generic entity routes */}
            <Route path="media/nouveau" element={<MediaUploadPage />} />
            <Route path=":entity" element={<EntityListPage />} />
            <Route path=":entity/nouveau" element={<EntityEditorPage />} />
            <Route path=":entity/:id" element={<EntityEditorPage />} />
          </Route>

          <Route path="*" element={<CmsPagePublic />} />
        </Routes>
      </Suspense>
      {/* Lancelot : Widget CMS pour l'extranet, Widget public sur le site (contrôlé par assistant.enabled_public) */}
      <Suspense fallback={null}>
        <AssistantWidget />
      </Suspense>
      {!isExtranet && <CartDrawer />}
      <CookieConsentBanner />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
            {process.env.REACT_APP_PUBLIC_INDEXING !== "true" && (
              <Helmet>
                <meta name="robots" content="noindex,nofollow" />
              </Helmet>
            )}
            <AppRouter />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#F8F6F2",
                  color: "#3F5B43",
                  border: "1px solid #D8D1C6",
                  borderRadius: "2px",
                  fontFamily: "Inter, sans-serif",
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
